import React, { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  Paper,
  InputBase,
  Divider,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Send as SendIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
} from "@mui/icons-material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import {
  fetchComments,
  addComment,
  updateComment,
  deleteComment,
} from "../../../service/firebaseService";
import { useMsal } from "@azure/msal-react";
import CommentsComponent from "./CommentsComponent";
import { useAuthContext } from "../../../context/authContext";
import { sendCommentNotification } from "../../../pages/client/AllEquipments/CommonFeatures/DCSTab/Table/commentsApi";

const CommentsDialog = ({
  sender,
  open,
  handleClose,
  parentId,
  parentType,
  isNotDialog,
  senderName,
  participants = [],
  raciId = null,
}) => {
  const [commentsData, setCommentsData] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editCommentId, setEditCommentId] = useState(null);
  const [newAttachment, setNewAttachment] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const authContextUser = useAuthContext()?.user;
  const user = authContextUser || sender || { name: "Unknown User" };

  console.log("userrr", user.name);

  const name = user.name;
  useEffect(() => {
    const fetchData = async () => {
      try {
        const comments = await fetchComments(parentId, parentType);
        setCommentsData(
          comments.map((comment) => ({
            ...comment,
            timestamp: comment.timestamp?.toDate() || new Date(),
          }))
        );
      } catch (error) {
        console.error("Error fetching comments:", error);
      }
    };

    if (open) fetchData();
  }, [open, parentId, parentType]);

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const handleAddOrEditComment = async () => {
    if (newComment.trim() || newAttachment) {
      setLoading(true);
      try {
        if (editCommentId) {
          await updateComment(editCommentId, newComment.trim(), newAttachment);
          setCommentsData((prev) =>
            prev.map((c) =>
              c.id === editCommentId
                ? {
                    ...c,
                    commentText: newComment.trim(),
                    attachment: imagePreview || c.attachment,
                  }
                : c
            )
          );
        } else {
          // Add the comment
          await addComment(
            parentId,
            parentType,
            newComment.trim(),
            name,
            newAttachment,
            replyTo
          );
          setCommentsData((prev) => [
            ...prev,
            {
              id: Date.now().toString(),
              commentText: newComment.trim(),
              attachment: imagePreview,
              userName: name,
              timestamp: new Date(),
              parentCommentId: replyTo || null,
            },
          ]);

          // Send notification emails to all participants when a new comment is added
          // Only send if this is a RACI comment and we have participants
          if (
            parentType === "RACI" &&
            raciId &&
            participants &&
            participants.length > 0
          ) {
            try {
              await sendCommentNotification(
                raciId,
                newComment.trim(),
                name
              );
              console.log("Comment notification emails sent to participants");
            } catch (error) {
              // Don't block the comment creation if notification fails
              console.error("Failed to send comment notification emails:", error);
            }
          }
        }

        resetForm();
      } catch (error) {
        console.error("Error adding/editing comment:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteComment = async (commentId) => {
    setLoading(true);
    try {
      await deleteComment(commentId);
      setCommentsData((prev) =>
        prev.filter((comment) => comment.id !== commentId)
      );
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewAttachment(file);
      setImagePreview(
        file.type === "application/pdf" ? null : URL.createObjectURL(file)
      );
    }
  };

  const handlePaste = (event) => {
    const items = event.clipboardData?.items;
    if (items) {
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            setNewAttachment(file);
            setImagePreview(URL.createObjectURL(file));
          }
          break;
        }
      }
    }
  };

  useEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener("paste", handlePaste);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("paste", handlePaste);
      }
    };
  }, []);

  const resetForm = () => {
    setNewComment("");
    setNewAttachment(null);
    setImagePreview(null);
    setEditCommentId(null);
    setReplyTo(null);
  };

  const topLevelComments = commentsData.filter((c) => !c.parentCommentId);
  const repliesByParent = commentsData.reduce((acc, comment) => {
    if (comment.parentCommentId) {
      if (!acc[comment.parentCommentId]) acc[comment.parentCommentId] = [];
      acc[comment.parentCommentId].push(comment);
    }
    return acc;
  }, {});

  return isNotDialog ? (
    <Box>
      <DialogContent onPaste={handlePaste}>
        <CommentsComponent
          topLevelComments={topLevelComments}
          setEditCommentId={setEditCommentId}
          setNewComment={setNewComment}
          handleDeleteComment={handleDeleteComment}
          setReplyTo={setReplyTo}
          repliesByParent={repliesByParent}
          replyTo={replyTo}
          imagePreview={imagePreview}
          inputRef={inputRef}
          editCommentId={editCommentId}
          newComment={newComment}
          loading={loading}
          fileInputRef={fileInputRef}
          handleAddOrEditComment={handleAddOrEditComment}
          handleFileChange={handleFileChange}
        />
      </DialogContent>
    </Box>
  ) : (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogContent onPaste={handlePaste}>
        <CommentsComponent
          topLevelComments={topLevelComments}
          setEditCommentId={setEditCommentId}
          setNewComment={setNewComment}
          handleDeleteComment={handleDeleteComment}
          setReplyTo={setReplyTo}
          repliesByParent={repliesByParent}
          replyTo={replyTo}
          imagePreview={imagePreview}
          inputRef={inputRef}
          editCommentId={editCommentId}
          newComment={newComment}
          loading={loading}
          fileInputRef={fileInputRef}
          handleAddOrEditComment={handleAddOrEditComment}
          handleFileChange={handleFileChange}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CommentsDialog;
